import React, { FunctionComponent, useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Guid } from "../../../api/baseApi";
import { deletePlayer, getPlayers, PlayerModel, PlayerStatus, UpdateConfigurationRequest, updateConfuguration } from "../../../api/playersApi";
import { convertUTCDateToLocalDate, formatDate, formatTime } from "../../../services/dateService";
import IconButton from "../../components/iconButton/IconButton";
import InnerLayout from "../innerLayout/InnerLayout";
import "./Players.css";
import './../../styles/table.css'
import { createPathWithId,  Routes } from "../../../constants/routes";
import GoogleMapReact from 'google-map-react'
import SvgSelector from "../../components/svgSelector/SvgSelector";
import PlayerStatistic, { PlayerGroup } from "../../components/playerStatistic/PlayerStatistic";
import Search from "../../components/search/Search";
import GlobalSpinner from "../../components/globalsSpinner/GlobalSpinner";
import SortIndicator from "../../components/sortIndicator/SortIndicator";
import { SmallModal } from "../../components/sideModal/Modal";
import { Button } from "../../components/Button";
import Pagination from "../../components/pagination/Pagination";
import { getPagesCount } from "../../../services/paginator";

export interface PlayersProps  extends RouteComponentProps<any>
{
    logOut : () => void;
    toogleCollapse: () => void;
    isCollapsed: boolean;
}

interface PlayerPinProps {
    lat: number,
    lng: number,
    text: string,
    status: "online" | "offline"
}

type SortBy = "" | "name" | "location";
type FilterByEnabled = "all" | "enabled" | "disabled";

const PlayerPin = (props: PlayerPinProps) => {
    const getColor = () => {
        switch(props.status){
            case "offline" : return "#FF614C";
            case "online"  : return  "#4F97DA";
        }
    }

    return (  
        <>
        <span>
            <SvgSelector title="marker" color={getColor()}></SvgSelector>
            <span>{props.text}</span>
        </span>     
        </>
    );
}

const Players: FunctionComponent<PlayersProps> = (props: PlayersProps) => {
    let {logOut, toogleCollapse, isCollapsed, history} = props;

    let [ players, setPlayers ] = useState<PlayerModel[]>([]);
    let [ isLoading, setIsLoading ] = useState<boolean>(false);
    let [ filterGroup, setFilterGroup] = useState<PlayerGroup>('total');
    let [ filteredPlayers, setFilteredPlayers] = useState<PlayerModel[]>([]);
    let [ currentPage, setCurrentPage] = useState<number>(1);
    let [ playersPerPage, setPlayersPerPage] = useState<PlayerModel[]>([]);
    let [ searchString, setSearchString] = useState<string>('');
    let [ sortBy, setSortBy] = useState<SortBy>('');
    let [ isAscSort, setIsAscSort] = useState<boolean>(false);
    let [ isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false);
    let [ playerIdForDelete, setPlayerIdForDelete] = useState<Guid>('');
    let [ filterByEnabledStatus, setFiltedByEnabled] = useState<FilterByEnabled>("all");

    let [gMap, setGMap] = useState<any>(undefined);
    let [gMaps, setGMaps] = useState<any>(undefined);

    const itemsPerPage = 20;

    useEffect(() => {
        async function fetchData(){
            setIsLoading(true);
            await Promise.all([updatePlayers()])
            setIsLoading(false);
        }

        fetchData();
    }, []);

    useEffect(() => {
        let filteredBySearch = !!!searchString ? players : players.filter(it => {
            return !!it.name && it.name.toLowerCase().includes(searchString.toLowerCase()) ||
            !!it.location && it.location.toLowerCase().includes(searchString.toLowerCase()) ||
            !!it.channel && it.channel.name.toLowerCase().includes(searchString.toLowerCase()) ||
            !!it.id && it.id.toLowerCase().includes(searchString.toLowerCase())
        });


        let filterByGroup: (player: PlayerModel) => boolean;
        switch(filterGroup){
            case "total" : filterByGroup = it => filterByEnabled(it); break;
            case "online" : filterByGroup = it => it.isOnline && filterByEnabled(it); break;
            case "offline" : filterByGroup = it => !it.isOnline && filterByEnabled(it); break;
            case "withAssignedChannel" : filterByGroup = it => it.channel !== null && filterByEnabled(it); break;
            case "withoutChannel" : filterByGroup = it => it.channel === null && filterByEnabled(it); break;
        };

        let sortByParam;
        switch(sortBy){
            case "name" : sortByParam = sortArrayByName; break;
            case "location" : sortByParam = sortArrayByLocation; break;
        }

        setFilteredPlayers(filteredBySearch.filter(filterByGroup).sort(sortByParam));
        setCurrentPage(1);
    }, [filterGroup, players, searchString, sortBy, isAscSort, filterByEnabledStatus]);

    let filterByEnabled = (player: PlayerModel) => {
        switch (filterByEnabledStatus) {
            case "all" : return true;
            case "disabled" : return !player.isEnabled;
            case "enabled" : return player.isEnabled;
        }
    }

    useEffect(() => {
        let startIndex = (currentPage - 1)*itemsPerPage;
        let finishIndex = startIndex + itemsPerPage;
        if(gMap){
            const bounds = getMapBounds(gMap, gMaps);
            gMap.fitBounds(bounds);
        }
    }, [filteredPlayers, filterByEnabledStatus]);

    useEffect(() => {
        let startIndex = (currentPage - 1)*itemsPerPage;
        let finishIndex = startIndex + itemsPerPage;
        setPlayersPerPage(filteredPlayers.slice(startIndex, finishIndex));
    }, [currentPage, filteredPlayers, filterByEnabledStatus]);

    const updatePlayers = async () => {
        setIsLoading(true);
        var players = await getPlayers();
        setPlayers(players.players);
        setIsLoading(false);
    }

    const getPlayersStats = () => {
        let filteredByEnabled = players.filter(it => filterByEnabled(it));

        let total = filteredByEnabled?.length;
        let active = filteredByEnabled?.filter(it => it.isOnline).length;
        let notActive = total - active;
        let withAssignedChannel =  filteredByEnabled?.filter(it => it.channel !== null).length;
        let withoutChannel =  total - withAssignedChannel;

        return { total, active, notActive, withAssignedChannel, withoutChannel };
    }

    const filterPlayers = (group: PlayerGroup) => {
        setFilterGroup(group);
    }
    
    // Return map bounds based on list of places
    const getMapBounds = (map: any, maps: any) => {
    var places = filteredPlayers.filter(it => !!it.long);
    const bounds = new maps.LatLngBounds();
  
    places.forEach((place) => {
      bounds.extend(new maps.LatLng(
        place.lat,
        place.long,
      ));
    });
    return bounds;
  };

  const gMapLoaded = (map: any, maps: any) => {    
    const bounds = getMapBounds(map, maps);
    map.fitBounds(bounds);

    setGMap(map); setGMaps(maps);
  };

    const map = () => {
        return <GoogleMapReact
                defaultCenter={{lat: 51.5072, lng:  -0.1076}}
                defaultZoom={13}
                bootstrapURLKeys={{key:"AIzaSyB2UbKb3oepkyMSa1Se1yDdXYTOvfaZEDk"}}
                onGoogleApiLoaded={({ map, maps }) => gMapLoaded(map, maps) }
              >
                  {
                    filteredPlayers.filter(it => !!it.long).map(it => <PlayerPin text={it.name} lat={it.lat} lng={it.long} status={it.isOnline ? "online" : "offline"}/>)
                  }
            </GoogleMapReact>
    }

    const renderPlayerStatus  = (status: PlayerStatus) => {
        switch (status) {
            case PlayerStatus.Online : return 'ok';
            case PlayerStatus.Offline : return 'error';
            case PlayerStatus.OutOfDayParting : return 'out'
        }
    }

    const renderPlayerRow = (player : PlayerModel) => {
        const statusClass = `status ${renderPlayerStatus(player.playerStatus)}`;
        let isNew = !!!player.name || !!!player.location;
        return <tr key={player.id}>
            <td><span className={statusClass}></span><span className="player-name">{player.name} {isNew && <span className="new">new</span>}</span></td>
            <td>{player.id}</td>
            <td>{player.channel?.name}</td>
            <td>{player.location}</td>
            <td>
                {convertUTCDateToLocalDate(player.lastSeen)}
            </td>
            <td>
                {player.version}
            </td>
            <td>
                <IconButton tooltipText="Edit" type="edit" onClick={()=>{history.push(createPathWithId(Routes.Players, player.id))}} />
                <IconButton tooltipText="Delete" type="delete" onClick={()=>showDeleteModal(player.id)} />
            </td>             
        </tr>
    }

    const playerStats = getPlayersStats();

    const sortArrayByName = (x: PlayerModel, y: PlayerModel) =>{
        let xName = x.name == null ? '' : x.name;
        let yName = y.name == null ? '' : y.name;
        let temp = xName > yName ? 1 : -1;
        if(!isAscSort) temp*=-1;
        return temp
    }

    const sortArrayByLocation = (x: PlayerModel, y: PlayerModel) => {
        let xLocation = x.location == null ? '' : x.location;
        let yLocation = y.location == null ? '' : y.location;
        let temp = xLocation > yLocation ? 1 : -1;
        if(!isAscSort) temp*=-1;
        return temp
    }
    
    const checkCurrentSortColumn = (sortBy: SortBy, currentSort: SortBy) => sortBy === currentSort
    
    const setSortData = (sortByNew: SortBy) => {
        if(sortByNew !== sortBy){
            setIsAscSort(true);
            setSortBy(sortByNew);
        }
        else {
            setIsAscSort(!isAscSort);
        }
    }

    const showDeleteModal = (playerId: Guid) => {
        setIsShowDeleteModal(true);
        setPlayerIdForDelete(playerId);
    }
    const closeDeleteModal = () => setIsShowDeleteModal(false);

    const deleteCurrentPlayer = async () =>{
        setIsLoading(true);
        await deletePlayer(playerIdForDelete);
        await updatePlayers()
        setIsShowDeleteModal(false);
        setIsLoading(false);
    }

    let getfilterByEnabledCss = (filter: FilterByEnabled) => {
        return {marginRight:"10px", cursor: "pointer", color : filter == filterByEnabledStatus ? "#4F97DA" : "black" }
    }

    return (

    <>
        <InnerLayout 
                activeTab="players" 
                logOut={logOut} 
                toogleCollapse={toogleCollapse}
                isCollapsed={isCollapsed}
                >            
                <>
                
                    <div style={{"height": "30px"}}></div>
                    <div style={{marginBottom:"10px"}}>
                        <span onClick={() => setFiltedByEnabled("all")} style={getfilterByEnabledCss("all")}>All</span>
                        <span onClick={() => setFiltedByEnabled("disabled")} style={getfilterByEnabledCss("disabled")}>Disabled</span>
                        <span onClick={() => setFiltedByEnabled("enabled")} style={getfilterByEnabledCss("enabled")}>Enabled</span>
                    </div>
                    <PlayerStatistic 
                        activeGroup={filterGroup}
                        total={playerStats.total}
                        online={playerStats.active}
                        offline={playerStats.notActive}
                        assignedPlayers={playerStats.withAssignedChannel}
                        unassignedPlayers={playerStats.withoutChannel}
                        onClickFunction={filterPlayers}
                    />
                    {players.length != 0 &&
                    <>
                    <div className="map-wrapper">
                        {map()}
                    </div>

                    <Search value={searchString} onChangeValue={setSearchString} />

                    <table className="table player-table">
                        <thead>
                            <tr>
                                <td className="sort-cursor" onClick={()=>{setSortData("name")}}>Player Name <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("name", sortBy)} /></td>
                                <td>Player ID</td>
                                <td>Network</td>
                                <td className="sort-cursor" onClick={()=>{setSortData("location")}}>Location <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("location", sortBy)} /></td>
                                <td>Hearbeat</td>
                                <td>Version</td>
                                <td></td>
                            </tr>
                        </thead>
                        <tbody>
                            {playersPerPage.map(p => renderPlayerRow(p))}    
                        </tbody>
                    </table>  
                    <Pagination activePage={currentPage} pagesCount={getPagesCount(filteredPlayers.length, itemsPerPage)} changePaginationPage={setCurrentPage} />
                    </>                   
                    }
                    <SmallModal show={isShowDeleteModal} onRequestClose={closeDeleteModal}> 
                        <p>That you want to delete player with ID - <b>{playerIdForDelete}</b>?</p>
                        <div className="modal-btn-group">
                            <Button color="gray" label="Cancel" onClick={closeDeleteModal}/>
                            <Button color="green" label="Yes" onClick={deleteCurrentPlayer}/>
                        </div>
                    </SmallModal>
                    <GlobalSpinner isShow={isLoading} />
                </>
            </InnerLayout>
    </>)   
}

export default withRouter(Players);